import { useCallback, useState } from 'react';

type UseTranslation = ({
  defaultTranslations,
  initialTranslations,
}: {
  defaultTranslations: Record<string, string>;
  initialTranslations: Record<string, string>;
}) => (key: string) => string;

/**
 * _app.getInitialProps is the single source of the page's translations: it resolves the language
 * for PMT pages and for SEO tracking pages (/pl/track-opek) alike, and re-runs on every client-side
 * navigation. Nothing is fetched here on purpose — a second, pathname-derived language would have
 * to repeat that resolution exactly, and any mismatch lets a late response overwrite the page's
 * strings with another language's.
 */
const useTranslations: UseTranslation = ({ defaultTranslations, initialTranslations }) => {
  const [translations, setTranslations] = useState<Record<string, string>>(initialTranslations);
  const [appliedInitialTranslations, setAppliedInitialTranslations] = useState(initialTranslations);

  // useState only reads its argument on mount, so each new page's translations are copied in
  // during render — otherwise a client-side hop to another locale keeps the previous page's
  // strings until a reload.
  if (initialTranslations !== appliedInitialTranslations) {
    setAppliedInitialTranslations(initialTranslations);
    if (Object.keys(initialTranslations).length > 0) {
      setTranslations(initialTranslations);
    }
  }

  return useCallback(
    (key: string) => {
      return translations[key] || defaultTranslations[key] || key;
    },
    [defaultTranslations, translations],
  );
};

export default useTranslations;
